// Client Care Command Centre — the redesigned Post-Settlement CRM.
// Feels like a premium fintech control room, not a template CRM.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Users, ChevronLeft, Search, Loader2, AlertCircle, Flame, Sparkles,
  ClipboardList, TrendingUp, BarChart3, Filter, Clock, PhoneCall,
  Mail, MessageSquare, Radar, Zap, ChevronRight, CheckCircle2, ArrowUpRight,
  Coins, Repeat, Home as HomeIcon, DollarSign, Send, Timer, HeartHandshake,
  ShieldAlert, TrendingDown, Building2, CalendarClock, Star,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { QuickNoteModal, BrokerHandoffModal } from '../components/post_settlement/Modals';

const API = process.env.REACT_APP_BACKEND_URL;
const BRAND = '#0057FF';
const HERO_BG = 'https://images.unsplash.com/photo-1614850523011-8f49ffc73908?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwxfHxkYXJrJTIwYmx1ZSUyMGFic3RyYWN0JTIwZ3JhZGllbnQlMjB0ZXh0dXJlfGVufDB8fHx8MTc4MzMzMjAyNnww&ixlib=rb-4.1.0&q=85';

// ═══════════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════════
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return iso; }
};
const fmtRel = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const diff = Math.round((Date.now() - d.getTime()) / 86400000);
    if (diff === 0) return 'today';
    if (diff === 1) return 'yesterday';
    if (diff > 1) return `${diff}d ago`;
    if (diff === -1) return 'tomorrow';
    return `in ${-diff}d`;
  } catch { return iso; }
};
const fmtMoney = (n) => `$${Math.round(Number(n) || 0).toLocaleString('en-AU')}`;

// Frontend care-score & health-status derivations (spec from design guidelines)
const daysSince = (iso) => { if (!iso) return null; try { return Math.floor((Date.now() - new Date(iso).getTime()) / 86400000); } catch { return null; } };
const daysUntil = (iso) => { if (!iso) return null; try { return Math.floor((new Date(iso).getTime() - Date.now()) / 86400000); } catch { return null; } };

const computeCareScore = (c) => {
  const contactedRecent = c?.last_activity?.created_at || c?.last_contact_at;
  const ds = daysSince(contactedRecent);
  const contactedOK = ds !== null && ds <= 90;

  const nextDue = c?.next_task?.scheduled_at ? daysUntil(c.next_task.scheduled_at) : null;
  const noOverdue = nextDue === null || nextDue >= 0;

  const npsCat = c?.latest_nps?.category || c?.last_nps_category;
  const npsScore = npsCat === 'Promoter' ? 20 : npsCat === 'Passive' ? 10 : 0;

  const hasOpp = (c?.open_opps || []).length > 0;
  const referral = (c?.open_opps || []).some((o) => (o.opportunity_type || o.type) === 'referral');

  return (contactedOK ? 30 : 0) + (noOverdue ? 25 : 0) + npsScore + (hasOpp ? 15 : 0) + (referral ? 10 : 0);
};

const HEALTH_META = {
  healthy:         { label: 'Healthy',         cls: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200/60', dot: 'bg-emerald-500' },
  needs_attention: { label: 'Needs attention', cls: 'bg-amber-50  text-amber-800  ring-1 ring-amber-200/60',   dot: 'bg-amber-500'   },
  overdue:         { label: 'Overdue',         cls: 'bg-rose-50   text-rose-700   ring-1 ring-rose-200/60',    dot: 'bg-rose-500'    },
  opportunity:     { label: 'Opportunity',     cls: 'bg-violet-50 text-violet-700 ring-1 ring-violet-200/60',  dot: 'bg-violet-500'  },
  broker_review:   { label: 'Broker review',   cls: 'bg-blue-50   text-blue-700   ring-1 ring-blue-200/60',    dot: 'bg-blue-500'    },
  at_risk:         { label: 'At risk',         cls: 'bg-red-50    text-red-700    ring-1 ring-red-200/60',     dot: 'bg-red-500'     },
  dnc:             { label: 'Do not contact',  cls: 'bg-slate-100 text-slate-500  ring-1 ring-slate-200',      dot: 'bg-slate-400'   },
};

const computeHealth = (c) => {
  if (c?.suppression_dnc || c?.do_not_contact) return 'dnc';
  const derived = c?.derived_status;
  if (['rate_review', 'refinance', 'equity', 'new_purchase'].includes(derived)) return 'opportunity';
  if (derived === 'broker_review') return 'broker_review';
  if (derived === 'lost_contact') return 'at_risk';
  const nps = c?.latest_nps?.category || c?.last_nps_category;
  if (nps === 'Detractor') return 'at_risk';
  const dueDays = c?.next_task?.scheduled_at ? daysUntil(c.next_task.scheduled_at) : null;
  if (dueDays !== null && dueDays < 0) return 'overdue';
  if (dueDays !== null && dueDays <= 3) return 'needs_attention';
  if (derived === 'first_checkin_due') return 'needs_attention';
  return 'healthy';
};

// ═══════════════════════════════════════════════════════════════════
// TOP-LEVEL PAGE
// ═══════════════════════════════════════════════════════════════════
export default function PostSettlementCRM() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('dashboard');
  const authHeaders = useMemo(() => ({
    Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
  }), []);

  const TABS = [
    { key: 'dashboard', label: 'Command Centre', icon: Radar },
    { key: 'clients',   label: 'Clients',        icon: Users },
    { key: 'alerts',    label: 'Priority',       icon: Flame },
    { key: 'reports',   label: 'Reports',        icon: TrendingUp },
  ];

  return (
    <div className="min-h-screen bg-[var(--page-bg)]">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <button type="button" onClick={() => navigate('/')} data-testid="ps-back"
                className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-slate-500 hover:text-slate-900 transition-colors mb-4">
          <ChevronLeft className="w-4 h-4" /> Back to CRM
        </button>

        {/* Page brand */}
        <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
          <div>
            <div className="flex items-center gap-2 mb-1.5">
              <span className="w-9 h-9 rounded-2xl bg-gradient-to-br from-emerald-100 to-teal-50 border border-emerald-200/60 flex items-center justify-center">
                <HeartHandshake className="w-[18px] h-[18px] text-emerald-700" strokeWidth={2.2} />
              </span>
              <span className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">Derwent · Client Care</span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-slate-950">Command Centre</h1>
            <p className="text-sm text-slate-500 mt-1 max-w-lg">Every settled client, cared for. Every 3-month check-in, on time. Every opportunity, spotted early.</p>
          </div>

          {/* Tabs — segmented pill */}
          <div className="flex gap-1 bg-white rounded-full p-1 border border-slate-200/80 shadow-sm">
            {TABS.map((t) => (
              <button
                key={t.key} type="button"
                onClick={() => setTab(t.key)}
                data-testid={`ps-tab-${t.key}`}
                className={`relative inline-flex items-center gap-1.5 rounded-full text-[12px] font-bold px-3 sm:px-4 py-2 transition-all ${
                  tab === t.key ? 'text-white' : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                {tab === t.key && (
                  <motion.span layoutId="ps-tab-bg" className="absolute inset-0 rounded-full shadow-sm" style={{ backgroundColor: '#0f172a' }} transition={{ type: 'spring', stiffness: 340, damping: 30 }} />
                )}
                <span className="relative z-10 flex items-center gap-1.5"><t.icon className="w-3.5 h-3.5" /> <span className="hidden sm:inline">{t.label}</span></span>
              </button>
            ))}
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div key={tab} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.22 }}>
            {tab === 'dashboard' && <Dashboard authHeaders={authHeaders} onOpenClient={(id) => navigate(`/post-settlement/clients/${id}`)} onGoto={setTab} />}
            {tab === 'clients'   && <ClientList authHeaders={authHeaders} onOpenClient={(id) => navigate(`/post-settlement/clients/${id}`)} />}
            {tab === 'alerts'    && <PriorityAlerts authHeaders={authHeaders} onOpenClient={(id) => navigate(`/post-settlement/clients/${id}`)} />}
            {tab === 'reports'   && <Reports authHeaders={authHeaders} />}
          </motion.div>
        </AnimatePresence>
      </main>
      <Footer />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// DASHBOARD — command centre
// ═══════════════════════════════════════════════════════════════════
const Dashboard = ({ authHeaders, onOpenClient, onGoto }) => {
  const [data, setData] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [d, a] = await Promise.all([
        axios.get(`${API}/api/post-settlement/dashboard`, { headers: authHeaders }),
        axios.get(`${API}/api/post-settlement/priority-alerts`, { headers: authHeaders }),
      ]);
      setData(d.data);
      setAlerts(a.data?.alerts || []);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to load dashboard'); }
    finally { setLoading(false); }
  }, [authHeaders]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <PageLoader />;
  if (!data) return null;
  const c = data.cards;

  // FOCUS items — the "what to do right now"
  const focus = [
    { label: 'clients due today',           n: c.due_today,       to: 'clients', icon: Timer,        tone: 'text-blue-100    bg-blue-500/20'    },
    { label: 'overdue',                     n: c.overdue,         to: 'clients', icon: AlertCircle,  tone: 'text-rose-100    bg-rose-500/25'    },
    { label: 'fixed rates expiring',        n: c.fixed_expiring,  to: 'clients', icon: CalendarClock,tone: 'text-amber-100   bg-amber-500/25'   },
    { label: 'flagged for broker review',   n: c.broker_review,   to: 'alerts',  icon: HeartHandshake,tone:'text-violet-100  bg-violet-500/25'  },
    { label: 'unhappy clients',             n: c.satisfaction_issues, to:'alerts',icon:ShieldAlert,  tone: 'text-red-100     bg-red-500/25'     },
  ];

  return (
    <div className="space-y-6">
      {/* ═══ TODAY'S CLIENT CARE FOCUS — Hero ═══ */}
      <motion.section
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
        className="brand-dark relative overflow-hidden rounded-3xl bg-slate-950 text-white shadow-[0_20px_60px_-15px_rgba(2,6,23,0.4)]"
        data-testid="ps-hero-focus"
      >
        {/* Background texture */}
        <div className="absolute inset-0 opacity-40 mix-blend-overlay" aria-hidden>
          <img src={HERO_BG} alt="" className="w-full h-full object-cover" />
        </div>
        {/* Radial glow */}
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-blue-500/20 blur-3xl" aria-hidden />
        <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-emerald-500/10 blur-3xl" aria-hidden />

        <div className="relative p-6 sm:p-8 lg:p-10">
          <div className="flex items-center gap-2 mb-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 backdrop-blur px-3 py-1 text-[10px] font-black uppercase tracking-[0.2em] text-white/90 ring-1 ring-white/15">
              <Sparkles className="w-3 h-3" /> Today&apos;s Client Care Focus
            </span>
            <span className="text-[11px] font-semibold text-white/70">{new Date().toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long' })}</span>
          </div>
          <h2 className="text-white text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight leading-[1.1]">
            {c.due_today + c.overdue > 0
              ? <>{c.due_today + c.overdue} client{(c.due_today + c.overdue) === 1 ? '' : 's'} need <span className="text-blue-300">your attention</span> today.</>
              : <>You&apos;re all caught up. <span className="text-emerald-300">Nice work.</span></>
            }
          </h2>
          <p className="text-sm text-white/80 mt-2 max-w-xl">Start with the highest-impact clients. Log each check-in as you go &mdash; the timeline builds itself.</p>

          <div className="mt-7 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {focus.map((f) => (
              <button key={f.label} type="button" onClick={() => onGoto(f.to)}
                data-testid={`ps-focus-${f.label.replace(/\s+/g, '-')}`}
                className="group text-left rounded-2xl p-4 bg-white/[0.10] hover:bg-white/[0.18] backdrop-blur-sm ring-1 ring-white/20 hover:ring-white/40 transition-all">
                <span className={`inline-flex w-8 h-8 rounded-xl items-center justify-center mb-3 ${f.tone}`}>
                  <f.icon className="w-4 h-4" strokeWidth={2.2} />
                </span>
                <p className="text-3xl font-black tracking-tight text-white leading-none">{f.n}</p>
                <p className="text-[11px] font-bold text-white/85 mt-1.5 leading-tight">{f.label}</p>
              </button>
            ))}
          </div>
        </div>
      </motion.section>

      {/* ═══ OPPORTUNITY RADAR ═══ */}
      <OpportunityRadar cards={c} onGoto={onGoto} />

      {/* ═══ Smart alerts strip + Recent completions ═══ */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <SmartAlertsCard alerts={alerts.slice(0, 5)} onOpenClient={onOpenClient} onSeeAll={() => onGoto('alerts')} />
        <RecentCompletions events={data.recent_completions} onOpenClient={onOpenClient} onSeeAll={() => onGoto('clients')} />
      </div>

      {/* ═══ Full stat grid (secondary) ═══ */}
      <section>
        <SectionTitle icon={BarChart3} eyebrow="At a glance" title="Team pulse" />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <StatChip label="Due this week"    value={c.due_week}         accent="blue"    icon={Timer}    testid="kpi-week" />
          <StatChip label="Due this month"   value={c.due_month}        accent="slate"   icon={Clock}    testid="kpi-month" />
          <StatChip label="Done today"       value={c.completed_today}  accent="emerald" icon={CheckCircle2} testid="kpi-completed-today" />
          <StatChip label="Done this week"   value={c.completed_week}   accent="emerald" icon={CheckCircle2} testid="kpi-completed-week" />
          <StatChip label="No contact"       value={c.no_contact}       accent="rose"    icon={AlertCircle} testid="kpi-no-contact" />
          <StatChip label="Broker review"    value={c.broker_review}    accent="violet"  icon={HeartHandshake} testid="kpi-broker" />
        </div>
      </section>
    </div>
  );
};

const OPP_ICONS = {
  rate_review:        { Ic: TrendingDown,  label: 'Rate reviews' },
  refinance:          { Ic: Repeat,        label: 'Refinances' },
  fixed_expiring:     { Ic: CalendarClock, label: 'Fixed expiring' },
  broker_review:      { Ic: HeartHandshake,label: 'Broker review' },
};

const OpportunityRadar = ({ cards, onGoto }) => {
  const items = [
    { k: 'rate_review',     n: cards.rate_review_opps, hint: 'Clients open to a repricing conversation.',   accent: 'from-violet-500/10 to-white ring-violet-200/70' },
    { k: 'refinance',       n: cards.refinance_opps,   hint: 'Warm leads exploring another lender.',        accent: 'from-blue-500/10   to-white ring-blue-200/70' },
    { k: 'fixed_expiring',  n: cards.fixed_expiring,   hint: 'Locked rates rolling off within 90 days.',    accent: 'from-amber-500/10  to-white ring-amber-200/70' },
    { k: 'broker_review',   n: cards.broker_review,    hint: 'Clients queued for the broker to speak with.',accent: 'from-emerald-500/10 to-white ring-emerald-200/70' },
  ];
  return (
    <section>
      <div className="flex items-end justify-between gap-3 mb-3">
        <SectionTitle icon={Radar} eyebrow="Opportunity Radar" title="Where growth is hiding" />
        <button type="button" onClick={() => onGoto('clients')} className="text-[12px] font-bold text-slate-600 hover:text-slate-900 inline-flex items-center gap-1">
          Open the pipeline <ArrowUpRight className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {items.map((it) => {
          const meta = OPP_ICONS[it.k];
          return (
            <motion.div key={it.k} whileHover={{ y: -3 }} transition={{ type: 'spring', stiffness: 300, damping: 22 }}
              className={`relative overflow-hidden rounded-2xl p-5 bg-gradient-to-br ${it.accent} ring-1 shadow-[0_2px_8px_-2px_rgba(15,23,42,0.06)]`}
              data-testid={`ps-radar-${it.k}`}>
              <div className="flex items-start justify-between">
                <span className="w-10 h-10 rounded-xl bg-white shadow-sm flex items-center justify-center">
                  <meta.Ic className="w-4 h-4 text-slate-900" strokeWidth={2.2} />
                </span>
                <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Open</span>
              </div>
              <p className="mt-4 text-4xl font-black tracking-tight text-slate-950">{it.n}</p>
              <p className="text-[13px] font-bold text-slate-800 mt-0.5">{meta.label}</p>
              <p className="text-[11px] text-slate-500 mt-1 leading-snug">{it.hint}</p>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
};

const SmartAlertsCard = ({ alerts, onOpenClient, onSeeAll }) => (
  <div className="lg:col-span-2 rounded-3xl p-5 sm:p-6 bg-white border border-slate-200/80 shadow-[0_2px_8px_-2px_rgba(15,23,42,0.05)]">
    <div className="flex items-start justify-between mb-4">
      <div>
        <p className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-400">Smart alerts</p>
        <p className="text-base font-black text-slate-900">What needs urgent action</p>
      </div>
      <button type="button" onClick={onSeeAll} className="text-[11px] font-bold text-slate-600 hover:text-slate-900 inline-flex items-center gap-1">
        See all <ArrowUpRight className="w-3.5 h-3.5" />
      </button>
    </div>
    {alerts.length === 0 ? (
      <div className="rounded-2xl bg-emerald-50/50 border border-emerald-100 p-6 text-center">
        <CheckCircle2 className="w-8 h-8 mx-auto text-emerald-500 mb-2" />
        <p className="text-sm font-bold text-emerald-900">Nothing on fire.</p>
        <p className="text-xs text-emerald-700/70 mt-0.5">Your client care is on track.</p>
      </div>
    ) : (
      <ul className="space-y-2">
        {alerts.map((a, i) => {
          const tone = a.severity === 'high' ? { pill: 'bg-rose-500', ring: 'ring-rose-100', bg: 'hover:bg-rose-50/60' } : { pill: 'bg-amber-500', ring: 'ring-amber-100', bg: 'hover:bg-amber-50/60' };
          return (
            <li key={i}>
              <button type="button" onClick={() => a.loan_id && onOpenClient(a.loan_id)}
                data-testid={`ps-dash-alert-${a.kind}-${i}`}
                className={`w-full text-left rounded-2xl p-3 ring-1 ${tone.ring} ${tone.bg} transition-colors flex items-center gap-3`}>
                <span className="relative flex-shrink-0">
                  <span className={`block w-2 h-2 rounded-full ${tone.pill}`} />
                  {a.severity === 'high' && <span className={`absolute -inset-1 rounded-full ${tone.pill} opacity-40 animate-ping`} />}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-[13px] font-bold text-slate-900 leading-tight truncate">{a.title}</p>
                  {a.detail && <p className="text-[11px] text-slate-500 truncate mt-0.5">{a.detail}</p>}
                </div>
                <ChevronRight className="w-4 h-4 text-slate-300" />
              </button>
            </li>
          );
        })}
      </ul>
    )}
  </div>
);

const RecentCompletions = ({ events, onOpenClient, onSeeAll }) => (
  <div className="rounded-3xl p-5 sm:p-6 bg-white border border-slate-200/80 shadow-[0_2px_8px_-2px_rgba(15,23,42,0.05)]">
    <div className="flex items-start justify-between mb-4">
      <div>
        <p className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-400">Recent wins</p>
        <p className="text-base font-black text-slate-900">Latest touchpoints</p>
      </div>
      <button type="button" onClick={onSeeAll} className="text-[11px] font-bold text-slate-600 hover:text-slate-900 inline-flex items-center gap-1">
        See all <ArrowUpRight className="w-3.5 h-3.5" />
      </button>
    </div>
    {(!events || events.length === 0) ? (
      <p className="text-xs text-slate-500">No completions logged yet.</p>
    ) : (
      <ul className="space-y-2">
        {events.map((t) => (
          <li key={t.id}>
            <button type="button" onClick={() => onOpenClient(t.loan_id)}
              className="w-full text-left rounded-2xl p-3 bg-slate-50/70 hover:bg-slate-100/70 border border-slate-200/60 flex items-center gap-3 transition-colors">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-[13px] font-bold text-slate-900 truncate">{t.client_name}</p>
                <p className="text-[11px] text-slate-500 truncate mt-0.5">{t.title} · {fmtRel(t.completed_at)}</p>
              </div>
              <ChevronRight className="w-3.5 h-3.5 text-slate-300" />
            </button>
          </li>
        ))}
      </ul>
    )}
  </div>
);

// ═══════════════════════════════════════════════════════════════════
// CLIENT LIST — client cards
// ═══════════════════════════════════════════════════════════════════
const ClientList = ({ authHeaders, onOpenClient }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [filters, setFilters] = useState({ status: '', overdue: false, fixed_expiry_days: '' });
  const [showFilters, setShowFilters] = useState(false);
  const [modalCtx, setModalCtx] = useState(null); // { type: 'note'|'handoff', loanId }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 300 };
      if (q) params.q = q;
      if (filters.status) params.status = filters.status;
      if (filters.overdue) params.overdue = true;
      if (filters.fixed_expiry_days) params.fixed_expiry_days = filters.fixed_expiry_days;
      const { data } = await axios.get(`${API}/api/post-settlement/clients`, { headers: authHeaders, params });
      setRows(data.clients || []);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to load'); }
    finally { setLoading(false); }
  }, [authHeaders, q, filters]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4">
      {/* Search + filter bar */}
      <div className="flex items-center gap-2">
        <div className="flex-1 relative">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search by client, loan number or lender…"
            data-testid="ps-search"
            className="w-full pl-10 pr-3 py-3 rounded-2xl outline-none text-sm bg-white border border-slate-200/80 focus:border-slate-300 focus:ring-2 focus:ring-blue-100 shadow-[0_2px_8px_-4px_rgba(15,23,42,0.06)]" />
        </div>
        <button type="button" onClick={() => setShowFilters(!showFilters)} data-testid="ps-toggle-filters"
                className={`inline-flex items-center gap-1.5 rounded-2xl text-[12px] font-bold px-4 py-3 border transition-all ${showFilters ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-800 border-slate-200/80 hover:border-slate-300'}`}>
          <Filter className="w-3.5 h-3.5" /> Filters
        </button>
      </div>

      <AnimatePresence>
        {showFilters && (
          <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }}
            className="rounded-2xl p-3 bg-white border border-slate-200/80 flex flex-wrap gap-2">
            <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                    data-testid="ps-filter-status"
                    className="rounded-xl text-xs font-semibold bg-slate-50 border border-slate-200 px-3 py-2 outline-none">
              <option value="">All statuses</option>
              <option value="newly_settled">Newly settled</option>
              <option value="first_checkin_due">First check-in due</option>
              <option value="active_3m">Active 3-month</option>
              <option value="rate_review">Rate review opp</option>
              <option value="refinance">Refinance opp</option>
              <option value="broker_review">Broker review</option>
              <option value="lost_contact">Lost contact</option>
              <option value="dnc">Do not contact</option>
            </select>
            <label className="inline-flex items-center gap-2 text-xs font-bold text-slate-700 rounded-xl bg-slate-50 border border-slate-200 px-3 py-2 cursor-pointer">
              <input type="checkbox" checked={filters.overdue} onChange={(e) => setFilters({ ...filters, overdue: e.target.checked })}
                     data-testid="ps-filter-overdue"
                     style={{ accentColor: BRAND }} />
              Overdue only
            </label>
            <select value={filters.fixed_expiry_days} onChange={(e) => setFilters({ ...filters, fixed_expiry_days: e.target.value })}
                    data-testid="ps-filter-fixed"
                    className="rounded-xl text-xs font-semibold bg-slate-50 border border-slate-200 px-3 py-2 outline-none">
              <option value="">Any fixed expiry</option>
              <option value="30">Fixed expiring ≤ 30d</option>
              <option value="60">Fixed expiring ≤ 60d</option>
              <option value="90">Fixed expiring ≤ 90d</option>
              <option value="180">Fixed expiring ≤ 180d</option>
            </select>
            {(filters.status || filters.overdue || filters.fixed_expiry_days) && (
              <button type="button" onClick={() => setFilters({ status: '', overdue: false, fixed_expiry_days: '' })}
                      className="text-xs font-bold text-slate-500 hover:text-slate-900 px-3 py-2">
                Clear
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{rows.length} client{rows.length === 1 ? '' : 's'}</p>

      {loading ? (
        <PageLoader />
      ) : rows.length === 0 ? (
        <div className="rounded-3xl p-12 text-center bg-white border border-slate-200/80">
          <Users className="w-10 h-10 mx-auto text-slate-300 mb-3" />
          <p className="text-lg font-black text-slate-900">No clients match</p>
          <p className="text-sm text-slate-500 mt-1">Try clearing filters or broadening the search.</p>
        </div>
      ) : (
        <motion.div initial="hidden" animate="show" variants={{ hidden: {}, show: { transition: { staggerChildren: 0.04 } } }}
                    className="grid grid-cols-1 gap-3">
          {rows.map((r) => (
            <ClientCard key={r.id} c={r}
              onOpen={() => onOpenClient(r.id)}
              onQuickNote={() => setModalCtx({ type: 'note', loanId: r.id })}
              onHandoff={() => setModalCtx({ type: 'handoff', loanId: r.id })}
            />
          ))}
        </motion.div>
      )}

      <AnimatePresence>
        {modalCtx?.type === 'note' && (
          <QuickNoteModal loanId={modalCtx.loanId} authHeaders={authHeaders}
            onClose={() => setModalCtx(null)}
            onSaved={() => { setModalCtx(null); load(); }} />
        )}
        {modalCtx?.type === 'handoff' && (
          <BrokerHandoffModal loanId={modalCtx.loanId} authHeaders={authHeaders}
            onClose={() => setModalCtx(null)}
            onSaved={() => { setModalCtx(null); load(); }} />
        )}
      </AnimatePresence>
    </div>
  );
};

const ClientCard = ({ c, onOpen, onQuickNote, onHandoff }) => {
  const health = computeHealth(c);
  const meta = HEALTH_META[health];
  const score = computeCareScore(c);
  const scoreTone = score >= 80 ? { ring: '#10b981', bg: 'bg-emerald-50', text: 'text-emerald-700' } :
                    score >= 50 ? { ring: '#f59e0b', bg: 'bg-amber-50',   text: 'text-amber-700'   } :
                                  { ring: '#ef4444', bg: 'bg-rose-50',    text: 'text-rose-700'    };
  const opp = (c.open_opps || [])[0];
  const oppLabel = opp ? ((opp.opportunity_type || opp.type) || '').replace(/_/g, ' ') : null;

  // Card is a clickable div (not a <button>) so quick-action <button>s can nest legally.
  const onKey = (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(); } };

  return (
    <motion.div variants={{ hidden: { opacity: 0, y: 6 }, show: { opacity: 1, y: 0 } }}>
      <div role="button" tabIndex={0} onClick={onOpen} onKeyDown={onKey}
        data-testid={`ps-row-${c.id}`}
        className="w-full group text-left cursor-pointer rounded-3xl bg-white border border-slate-200/80 hover:border-slate-300 shadow-[0_2px_8px_-4px_rgba(15,23,42,0.05)] hover:shadow-[0_14px_40px_-18px_rgba(15,23,42,0.18)] hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-blue-200 transition-all p-5">
        <div className="flex items-start gap-4">
          {/* Care Score gauge */}
          <div className="flex-shrink-0" data-testid={`ps-care-score-${c.id}`}>
            <CareScoreDial score={score} tone={scoreTone} size={64} />
          </div>

          {/* Main body */}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-base sm:text-lg font-black tracking-tight text-slate-950 truncate">{c.client_name}</p>
              {c.partner_name && <span className="text-[12px] text-slate-500">+ {c.partner_name}</span>}
              <span data-testid={`ps-health-${c.id}`}
                className={`inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full ${meta.cls}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} /> {meta.label}
              </span>
              {oppLabel && (
                <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full bg-violet-50 text-violet-700 ring-1 ring-violet-200/60">
                  <Sparkles className="w-3 h-3" /> {oppLabel}
                </span>
              )}
            </div>
            <p className="text-[11px] font-mono text-slate-400 mt-0.5">{c.loan_number || '—'}</p>

            {/* Data row */}
            <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5 text-[12px] text-slate-600">
              <Chip icon={Building2} value={c.lender_name || c.lender || '—'} />
              <Chip icon={DollarSign} value={fmtMoney(c.loan_amount)} />
              <Chip icon={Star} label="Settled" value={fmtDate(c.settlement_date)} />
              {c.last_activity?.created_at && <Chip icon={PhoneCall} label="Last contact" value={fmtRel(c.last_activity.created_at)} />}
              {c.next_task && <Chip icon={Timer} label="Next" value={`${c.next_task.title} · ${fmtRel(c.next_task.scheduled_at)}`} tone="text-blue-700" />}
            </div>

            {/* Quick actions — stop propagation so they don't trigger the card's onOpen */}
            <div className="mt-4 flex flex-wrap gap-1.5" onClick={(e) => e.stopPropagation()}>
              <QuickAction icon={Zap} label="Log check-in" onClick={onQuickNote} testid={`ps-qa-note-${c.id}`} primary />
              {c.mobile_number && <QuickAction icon={PhoneCall} label="Call" href={`tel:${c.mobile_number}`} testid={`ps-qa-call-${c.id}`} tone="emerald" />}
              {c.mobile_number && <QuickAction icon={MessageSquare} label="SMS" href={`sms:${c.mobile_number}`} testid={`ps-qa-sms-${c.id}`} tone="violet" />}
              {c.email && <QuickAction icon={Mail} label="Email" href={`mailto:${c.email}`} testid={`ps-qa-email-${c.id}`} tone="blue" />}
              <QuickAction icon={HeartHandshake} label="To broker" onClick={onHandoff} testid={`ps-qa-handoff-${c.id}`} tone="amber" />
              <QuickAction icon={ArrowUpRight} label="Open profile" onClick={onOpen} testid={`ps-qa-open-${c.id}`} tone="slate" />
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const Chip = ({ icon: Icon, label, value, tone = 'text-slate-600' }) => (
  <span className={`inline-flex items-center gap-1.5 ${tone}`}>
    <Icon className="w-3 h-3 text-slate-400" />
    {label && <span className="text-slate-400">{label}</span>}
    <span className="font-semibold text-slate-800">{value}</span>
  </span>
);

const QuickAction = ({ icon: Icon, label, onClick, href, testid, primary, tone = 'slate' }) => {
  const cls = primary
    ? 'bg-slate-900 text-white hover:bg-slate-800'
    : {
        emerald: 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100',
        violet:  'bg-violet-50 text-violet-700 hover:bg-violet-100',
        blue:    'bg-blue-50 text-blue-700 hover:bg-blue-100',
        amber:   'bg-amber-50 text-amber-800 hover:bg-amber-100',
        slate:   'bg-slate-100 text-slate-700 hover:bg-slate-200',
      }[tone];
  const inner = (
    <>
      <Icon className="w-3 h-3" strokeWidth={2.4} />
      <span className="hidden sm:inline">{label}</span>
    </>
  );
  const commonCls = `inline-flex items-center gap-1.5 rounded-full text-[11px] font-bold px-2.5 py-1.5 transition-colors ${cls}`;
  if (href) {
    return (
      <a href={href} onClick={(e) => e.stopPropagation()} data-testid={testid} className={commonCls}>
        {inner}
      </a>
    );
  }
  return (
    <button type="button" onClick={onClick} data-testid={testid} className={commonCls}>
      {inner}
    </button>
  );
};

const CareScoreDial = ({ score, tone, size = 56 }) => {
  const stroke = 5;
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (Math.min(100, Math.max(0, score)) / 100) * circ;
  return (
    <div className={`relative rounded-full ${tone.bg}`} style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} stroke="rgba(15,23,42,0.06)" strokeWidth={stroke} fill="none" />
        <circle cx={size / 2} cy={size / 2} r={r} stroke={tone.ring} strokeWidth={stroke} fill="none"
                strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
                style={{ transition: 'stroke-dashoffset 0.6s ease' }} />
      </svg>
      <div className={`absolute inset-0 flex flex-col items-center justify-center ${tone.text}`}>
        <span className="text-sm font-black leading-none">{score}</span>
        <span className="text-[8px] font-black uppercase tracking-widest leading-none mt-0.5">care</span>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// PRIORITY ALERTS (dedicated tab)
// ═══════════════════════════════════════════════════════════════════
const PriorityAlerts = ({ authHeaders, onOpenClient }) => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/post-settlement/priority-alerts`, { headers: authHeaders });
        setAlerts(data.alerts || []);
      } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
      finally { setLoading(false); }
    })();
  }, [authHeaders]);

  if (loading) return <PageLoader />;
  if (alerts.length === 0) return (
    <div className="rounded-3xl p-12 text-center bg-white border border-slate-200/80">
      <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-500 mb-3" />
      <p className="text-lg font-black text-slate-900">All clear</p>
      <p className="text-sm text-slate-500 mt-1">No priority alerts right now.</p>
    </div>
  );
  const high = alerts.filter(a => a.severity === 'high');
  const med  = alerts.filter(a => a.severity === 'medium');
  return (
    <div className="space-y-5">
      {high.length > 0 && <AlertGroup title="High priority" alerts={high} colour="rose" onOpenClient={onOpenClient} />}
      {med.length > 0  && <AlertGroup title="Medium priority" alerts={med}  colour="amber" onOpenClient={onOpenClient} />}
    </div>
  );
};

const AlertGroup = ({ title, alerts, colour, onOpenClient }) => (
  <div className="rounded-3xl p-5 sm:p-6 bg-white border border-slate-200/80 shadow-[0_2px_8px_-2px_rgba(15,23,42,0.05)]">
    <div className="flex items-center gap-2 mb-4">
      <span className={`w-7 h-7 rounded-xl flex items-center justify-center ${colour === 'rose' ? 'bg-rose-100' : 'bg-amber-100'}`}>
        <Flame className={`w-3.5 h-3.5 ${colour === 'rose' ? 'text-rose-600' : 'text-amber-600'}`} strokeWidth={2.4} />
      </span>
      <p className="text-base font-black text-slate-900">{title}</p>
      <span className="text-xs font-bold text-slate-400">{alerts.length}</span>
    </div>
    <div className="space-y-2">
      {alerts.map((a, i) => (
        <button key={i} type="button" onClick={() => a.loan_id && onOpenClient(a.loan_id)}
                data-testid={`ps-alert-${a.kind}-${i}`}
                className="w-full text-left rounded-2xl p-3.5 bg-slate-50/70 hover:bg-slate-100/70 border border-slate-200/60 flex items-start gap-3 transition-colors">
          <span className="relative mt-1">
            <span className={`block w-2 h-2 rounded-full ${colour === 'rose' ? 'bg-rose-500' : 'bg-amber-500'}`} />
            {colour === 'rose' && <span className="absolute -inset-1 rounded-full bg-rose-500 opacity-40 animate-ping" />}
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold text-slate-900 leading-tight">{a.title}</p>
            {a.detail && <p className="text-[11px] text-slate-500 mt-0.5 line-clamp-2">{a.detail}</p>}
          </div>
          <ChevronRight className="w-4 h-4 text-slate-400 mt-1" />
        </button>
      ))}
    </div>
  </div>
);

// ═══════════════════════════════════════════════════════════════════
// REPORTS
// ═══════════════════════════════════════════════════════════════════
const Reports = ({ authHeaders }) => {
  const [preset, setPreset] = useState('month');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/api/post-settlement/reports`, { headers: authHeaders, params: { preset } })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || 'Failed'))
      .finally(() => setLoading(false));
  }, [preset, authHeaders]);

  return (
    <div className="space-y-5">
      <div className="flex gap-1 bg-white rounded-full p-1 border border-slate-200/80 w-fit shadow-sm">
        {['today', 'week', 'month', 'last_month', 'quarter', 'year'].map((p) => (
          <button key={p} type="button" onClick={() => setPreset(p)}
                  data-testid={`ps-report-preset-${p}`}
                  className={`rounded-full text-[12px] font-bold px-3 py-1.5 transition-all ${
                    preset === p ? 'text-white shadow-sm' : 'text-slate-600 hover:text-slate-900'
                  }`}
                  style={preset === p ? { backgroundColor: '#0f172a' } : {}}>
            {p.replace('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
          </button>
        ))}
      </div>

      {loading || !data ? <PageLoader /> : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            <ReportCard label="Contacts made"       value={data.metrics.contacts_made}         icon={PhoneCall}    accent="emerald" />
            <ReportCard label="Opportunities"       value={data.metrics.opportunities_created} icon={Sparkles}     accent="violet" />
            <ReportCard label="Rate reviews"        value={data.metrics.rate_reviews_completed} icon={TrendingDown} accent="blue" />
            <ReportCard label="Refinances"          value={data.metrics.refinances_completed}  icon={Repeat}        accent="blue" />
            <ReportCard label="Referrals"           value={data.metrics.referrals}             icon={HeartHandshake} accent="amber" />
            <ReportCard label="Overdue snapshot"    value={data.metrics.overdue_snapshot}      icon={AlertCircle}  accent="rose" />
            <ReportCard label="DNC clients"         value={data.metrics.dnc_clients}           icon={ShieldAlert}  accent="slate" />
            <ReportCard label="Handoffs created"    value={data.metrics.handoffs_created}      icon={Send}         accent="slate" />
            <ReportCard label="Handoffs settled"    value={data.metrics.handoffs_settled}      icon={CheckCircle2} accent="emerald" />
            <ReportCard label="Handoff conversion"  value={`${data.metrics.handoff_conversion_pct}%`} icon={TrendingUp} accent="emerald" />
          </div>

          <div className="rounded-3xl p-5 sm:p-6 bg-white border border-slate-200/80 shadow-[0_2px_8px_-2px_rgba(15,23,42,0.05)]">
            <p className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-400">Team member activity</p>
            <p className="text-lg font-black text-slate-900 mb-4">Contacts by team member</p>
            {data.team_activity.length === 0 ? (
              <p className="text-xs text-slate-500">No contact activity in this range.</p>
            ) : (
              <div className="space-y-2">
                {data.team_activity.map((t) => {
                  const max = Math.max(...data.team_activity.map(x => x.contacts), 1);
                  const pct = Math.round((t.contacts / max) * 100);
                  return (
                    <div key={t.name} className="grid grid-cols-[1fr,2fr,auto] items-center gap-3">
                      <span className="text-sm font-semibold text-slate-800 truncate">{t.name}</span>
                      <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                        <motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.5 }}
                          className="h-full rounded-full bg-gradient-to-r from-blue-500 to-emerald-500" />
                      </div>
                      <span className="text-sm font-black text-slate-900 tabular-nums">{t.contacts}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const ReportCard = ({ label, value, icon: Icon, accent }) => {
  const tones = {
    emerald: 'text-emerald-600 bg-emerald-50',
    violet:  'text-violet-600 bg-violet-50',
    blue:    'text-blue-600 bg-blue-50',
    amber:   'text-amber-600 bg-amber-50',
    rose:    'text-rose-600 bg-rose-50',
    slate:   'text-slate-600 bg-slate-100',
  };
  return (
    <div className="rounded-2xl bg-white border border-slate-200/80 p-4 shadow-[0_2px_8px_-4px_rgba(15,23,42,0.05)]">
      <div className="flex items-center justify-between mb-3">
        <span className={`w-7 h-7 rounded-lg flex items-center justify-center ${tones[accent] || tones.slate}`}>
          <Icon className="w-3.5 h-3.5" strokeWidth={2.2} />
        </span>
      </div>
      <p className="text-2xl sm:text-3xl font-black tracking-tight text-slate-900">{value}</p>
      <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mt-1">{label}</p>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// SHARED
// ═══════════════════════════════════════════════════════════════════
const StatChip = ({ label, value, accent, icon: Icon, testid }) => {
  const tones = {
    emerald: 'text-emerald-700 bg-emerald-50',
    violet:  'text-violet-700 bg-violet-50',
    blue:    'text-blue-700 bg-blue-50',
    amber:   'text-amber-800 bg-amber-50',
    rose:    'text-rose-700 bg-rose-50',
    slate:   'text-slate-900 bg-slate-100',
  };
  return (
    <div data-testid={testid} className="rounded-2xl bg-white border border-slate-200/80 p-4 shadow-[0_2px_8px_-4px_rgba(15,23,42,0.05)]">
      <div className="flex items-center gap-2">
        <span className={`w-7 h-7 rounded-lg flex items-center justify-center ${tones[accent] || tones.slate}`}>
          <Icon className="w-3.5 h-3.5" strokeWidth={2.2} />
        </span>
        <p className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-400">{label}</p>
      </div>
      <p className="text-2xl sm:text-3xl font-black tracking-tight text-slate-900 mt-2">{value}</p>
    </div>
  );
};

const SectionTitle = ({ icon: Icon, eyebrow, title }) => (
  <div>
    <div className="flex items-center gap-2">
      <Icon className="w-3.5 h-3.5 text-slate-500" strokeWidth={2.4} />
      <span className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">{eyebrow}</span>
    </div>
    <p className="text-lg sm:text-xl font-black text-slate-900 mt-0.5">{title}</p>
  </div>
);

const PageLoader = () => (
  <div className="rounded-3xl p-16 bg-white border border-slate-200/80 text-center">
    <Loader2 className="w-6 h-6 animate-spin inline-block text-slate-400" />
  </div>
);
